import React from "react";
import { Dropdown, Tooltip } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  MoreOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { addRecentItem } from "utils/recentItems";
import { useAuth } from "contexts/AuthContext";
import { customStyles, statusColors } from "./TaskCard";

export const projectColors = [
  "#DBEAFE", // blue
  "#FEF3C7", // yellow
  "#FEE2E2", // red
  "#DCFCE7", // green
  "#EDE9FE", // purple
  "#FCE7F3", // pink
  "#E0F2FE", // sky
  "#FAE8FF", // fuchsia
];

export const getProjectColor = (id) => {
  const index = id % projectColors.length;
  return projectColors[index];
};

export const darkenColor = (hex, percent = 40) => {
  const num = parseInt(hex.replace("#", ""), 16);
  let r = (num >> 16) - percent;
  let g = ((num >> 8) & 0x00ff) - percent;
  let b = (num & 0x0000ff) - percent;

  r = r < 0 ? 0 : r;
  g = g < 0 ? 0 : g;
  b = b < 0 ? 0 : b;

  return `rgb(${r}, ${g}, ${b})`;
};

const ProjectCard = ({
  project,
  index,
  selectedTags,
  onTagClick,
  onView,
  onEdit,
  onDelete,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tagColor = getProjectColor(index);
  const textColor = darkenColor(tagColor, 60);

  const statusKeys = Object.keys(statusColors);
  const badgeStatus = statusKeys[index % statusKeys.length];
  const badgeStyle = statusColors[badgeStatus];

  const items = [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onView?.();
      },
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Edit",
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onEdit?.();
      },
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: "Delete",
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        onDelete?.();
      },
    },
  ];

  const handleOpenProject = () => {
    addRecentItem(user.id, {
      id: project.id,
      type: "project",
      title: project.name,
      path: `/general/projects/${project.id}`,
      openedAt: new Date(Date.now()).toISOString(),
    });

    navigate(`/general/projects/${project.id}`);
  };

  return (
    <div
      className="project-card"
      onClick={handleOpenProject}
      style={{ cursor: "pointer" }}
    >
      <div className="project-card-top">
        <span
          className="project-code"
          style={{
            ...customStyles,
            backgroundColor: badgeStyle.bg,
            color: badgeStyle.text,
            border: `1px solid ${badgeStyle.border}`,
          }}
        >
          <div
            className="project-avatar"
            style={{ backgroundColor: badgeStyle.text }}
          >
            {project.name.charAt(0)}
          </div>
          {project.code}
        </span>
        <h4 className="project-title text-sm mb-0 pb-0 mt-2">{project.name}</h4>
      </div>

      <Tooltip title={`Owner: ${project.ownerName}`}>
        <div className="owner-avatar">{project.ownerName.charAt(0)}</div>
      </Tooltip>

      {/* context menu */}
      <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
        <MoreOutlined
          onClick={(e) => e.stopPropagation()}
          style={{
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 16,
            position: "absolute",
            top: 20,
            right: 10,
          }}
        />
      </Dropdown>

      <div
        className="project-desc"
        style={{ color: "#374151", fontSize: 13 }}
        dangerouslySetInnerHTML={{
          __html: project.description || "<p>No description provided</p>",
        }}
      />
      <div className="task-divider" />

      <div className="project-meta">
        <div className="meta-item">
          <div className="meta-icon meta-blue">
            <CalendarOutlined />
          </div>
          <span>{project.iterationCount || 0} Iterations</span>
        </div>

        <div className="meta-item">
          <div className="meta-icon meta-purple">
            <FileTextOutlined />
          </div>
          <span>{project.taskCount || 0} Tasks</span>
        </div>
      </div>

      <div className="project-tags">
        {project.tags.slice(0, 3).map((tag, index) => {
          const isActive = selectedTags.includes(tag.name);

          return (
            <span
              key={index}
              className={`project-tag ${isActive ? "active-tag" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onTagClick(tag.name);
              }}
            >
              {tag.name}
            </span>
          );
        })}

        {project.tags.length > 3 && (
          <Tooltip title={project.tags.map((tag) => tag.name).join(", ")}>
            <span className="project-tag more-tag">
              +{project.tags.length - 3}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
